import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { getGroups, submitWaiver } from "../../services/RetreatServices";
import { Button, Form } from "react-bootstrap";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Spinner } from "react-bootstrap";
import AlertModal from "./AlertModal";

export default function WaiverForm() {
  const waiverImageUrl = `${process.env.PUBLIC_URL}/images/Retreat_Waiver_3.png`;
  const [groupData, setGroupData] = useState(null);
  const [formDataList, setFormDataList] = useState([]);
  const [useTypedSignature, setUseTypedSignature] = useState(false);
  const [typedSignature, setTypedSignature] = useState("");
  const [drawnSignature, setDrawnSignature] = useState(null);
  const signatureRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [currentMemberIndex, setCurrentMemberIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const registrationID = searchParams.get("registrationID");
  const retreatID = searchParams.get("retreatID");
  const filters = useMemo(
    () => ({ registrationID, retreatID }),
    [registrationID, retreatID]
  );
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!registrationID || !retreatID) {
      console.error("Missing query parameters: registrationID or retreatID");
      return;
    }

    getGroups(filters)
      .then((data) => {
        if (data && data.length > 0) {
          setGroupData(data[0]);
        } else {
          console.error("Participant data not found");
        }
      })
      .catch((error) =>
        console.error("Error fetching participant data:", error)
      );
  }, [filters]);
  const toggleSignatureType = () => {
    setUseTypedSignature((prev) => {
      if (!prev) {
        // Switching to Typed Signature: Save drawn signature before clearing
        if (signatureRef.current) {
          setDrawnSignature(signatureRef.current.toDataURL());
          signatureRef.current.clear();
        }
      } else {
        // Switching back to Drawn Signature: Restore the saved drawn signature
        if (drawnSignature && signatureRef.current) {
          const img = new Image();
          img.src = drawnSignature;
          img.onload = () => {
            const ctx = signatureRef.current.getCanvas().getContext("2d");
            ctx.drawImage(img, 0, 0);
          };
        }
      }
      return !prev;
    });
  };
  const handleCloseModal = () => {
    setShowSuccessModal(false);
    setSuccessMessage("");
    setError(null);
    setShowErrorModal(false);
  };
  const saveSignature = async () => {
    const currentMember = groupData?.groupMembers[currentMemberIndex];
    if (!currentMember) return false;

    console.log("currentMember", currentMember.firstName);

    let formData = new FormData();
    formData.append("retreatID", retreatID);
    formData.append("registrationID", registrationID);
    formData.append("memberID", currentMember.memberID);

    let signature;
    if (useTypedSignature) {
      if (!typedSignature.trim()) {
        alert("Please type your name as a signature before proceeding.");
        return false;
      }
      signature = {
        memberID: currentMember.memberID,
        type: "typed",
        value: typedSignature.trim(),
      };
    } else {
      if (!signatureRef.current || signatureRef.current.isEmpty()) {
        setError("Please sign the waiver before proceeding.");
        setShowErrorModal(true);
        return false;
      }
      signature = {
        memberID: currentMember.memberID,
        type: "drawn",
        value: signatureRef.current.toDataURL(),
      };
    }

    // Wait for the PDF to be generated before proceeding
    console.log("generatePDF ...");
    const pdfBlob = await generatePDF(currentMember, signature);
    console.log("generatePDF ... Done !!!");
    formData.append(
      "waiverPDF",
      pdfBlob,
      `${retreatID}_${registrationID}_${currentMember.firstName}_${currentMember.lastName}_waiver.pdf`
    );
    console.log("Adding formData ...", formData);
    console.log("Adding formData to formDataList...", formDataList);
    setFormDataList((prevFormDataList) => {
      const updatedList = [...prevFormDataList, formData];
      console.log("Added formData to formDataList...", updatedList);
      return updatedList;
    });
    return true;
  };

  // Other functions and component code...

  const handleNext = async () => {
    setLoading(true);
    const signatureSaved = await saveSignature();
    if (signatureSaved) {
      setTypedSignature(""); // Reset typed signature
      setCurrentMemberIndex((prevIndex) => prevIndex + 1);
      signatureRef?.current?.clear(); // Clear drawn signature
    }
    setLoading(false);
  };

  const generatePDF = async (currentMember, signature) => {
    try {
        const pdf = new jsPDF("p", "mm", "a4");  
        const content = document.getElementById("waiver-content");

        if (!content) {
            console.error("Waiver content not found!");
            return null;
        }

        // Wait for rendering
        window.scrollTo(0, 0);
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Capture content as canvas
        const canvas = await html2canvas(content, { scale: 2, useCORS: true, removeContainer: true });

        // Convert to image
        const imgData = canvas.toDataURL("image/png", 0.9);
        const imgWidth = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        pdf.addImage(imgData, "PNG", 0, 10, imgWidth, imgHeight);
        pdf.compress = true;

        // Ensure we return a proper Blob
        return new Promise((resolve) => {
            const pdfBlob = pdf.output("blob");
            if (pdfBlob instanceof Blob) {
                resolve(pdfBlob);
            } else {
                console.error("PDF output is not a Blob");
                resolve(null);
            }
        });
    } catch (error) {
        console.error("PDF Generation Error:", error);
        return null;
    }
};




  const handleSubmit = async () => {
    console.log("Submitting all waivers...");

    const signatureSaved = await saveSignature();

    console.log("signatureSaved", signatureSaved);
    if (!signatureSaved) {

      return;
    }
    let finalFormDataList = null;
    // Wait for state update before proceeding
    setTimeout(() => {
      console.log("Updated formDataList before submission:", formDataList); // Should now have data
      finalFormDataList = [...formDataList];
      try {
        console.log(
          "Updated formDataList before submission:",
          finalFormDataList
        ); // Should now have data
        if (finalFormDataList?.length === 0) {
          console.error("No waivers to submit! Generating PDF for Current Member");
          let newFormData = new FormData();
          const currentMember = groupData?.groupMembers[currentMemberIndex];
          console.log("currentMember", currentMember.firstName);
          newFormData.append("retreatID", retreatID);
          newFormData.append("registrationID", registrationID);
          newFormData.append("memberID", currentMember.memberID);
          for (let [key, value] of newFormData.entries()) {
            console.log(`newFormData Key: ${key}, Value:`, value);
          }
          const submitSingleWaiver = async () => {
            console.log("Submitting single waiver...");
            const signature = useTypedSignature
              ? {
                  memberID: currentMember.memberID,
                  type: "typed",
                  value: typedSignature.trim(),
                }
              : {
                  memberID: currentMember.memberID,
                  type: "drawn",
                  value: signatureRef?.current?.toDataURL(),
                };
            console.log("generatePDF ...");
            const pdfBlob = await generatePDF(currentMember, signature);
            console.log("generatePDF ... Done !!!");
            newFormData.append(
              "waiverPDF", // Add the generated PDF to the FormData object
              pdfBlob, // PDF Blob
              `${retreatID}_${registrationID}_${currentMember.firstName}_${currentMember.lastName}_waiver.pdf` // Filename
            );
            console.log("Submitting waiver:");
            for (let [key, value] of newFormData.entries()) {
              console.log(`newFormData Key: ${key}, Value:`, value);
            }
            setLoading(true);
            await submitWaiver(newFormData);
            setLoading(false);
          };
          submitSingleWaiver();
          setLoading(false);
        } else {
          setLoading(true);
          // Submit all collected FormData objects
          finalFormDataList.forEach(async (formData) => {
            console.log("Submitting waiver:", formData);
            await submitWaiver(formData);
          });
          setLoading(false);
        }
        setShowSuccessModal(true);
        setSuccessMessage("All waivers submitted successfully!");
        setLoading(false);
        navigate("/"); // Redirect to the home page
      } catch (error) {
        console.error("Error submitting waivers:", error);
        setError("Error submitting waivers. Please try again.");
        setShowErrorModal(true);
      }
      setLoading(false);
    }, 1000); // Small delay to allow state update
    setLoading(false);
  };

  if (!groupData) {
    return <p>Loading...</p>;
  }

  const currentMember = groupData?.groupMembers[currentMemberIndex];

  return (
    <div className="container mx-auto p-3 me-1">
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <div>
          {/* Responsive Form Content */}
          <div
            id="waiver-content"
            style={{
              width: "100%", // Responsive width
              maxWidth: "800px", // Ensure it looks good on all screens
              padding: "1.5rem",
              backgroundColor: "#fff",
              borderRadius: "8px",
              textAlign: "justify",
              fontFamily: "Arial, sans-serif",
              fontSize: "1rem",
              margin: "auto",
            }}
          >
            <img
              src={waiverImageUrl}
              alt="Waiver Form"
              style={{
                width: "100%",
                maxHeight: "150px",
                objectFit: "cover",
                marginBottom: ".5rem",
              }}
            />
            <h2
              style={{
                textAlign: "center",
                fontSize: "1.5rem",
                fontWeight: "bold",
              }}
            >
              Krupabhishekam Retreat 2025 Waiver Form
            </h2>
            <p style={{ textAlign: "center" }}>
              <strong>Event:</strong> Krupabhishekam Retreat 2025 at St. Mary’s
              Syro-Malabar Catholic Church, Old Bethpage, NY
            </p>
            <p style={{ textIndent: "1rem", fontSize: "0.9rem" }}>
              I willingly accept all risks, known and unknown, associated with
              participating in retreat activities, including those related to
              parking and the use of any shuttle or transportation services.
            </p>
            <p style={{ textIndent: "1rem", fontSize: "0.9rem" }}>
              I agree, for myself, my heirs, and assigns, to hold harmless and
              release any participating individuals, organizers, St. Mary’s
              Syro-Malabar Church, the Syro-Malabar Diocese of Chicago, and any
              other sponsoring organizations, including their respective
              subsidiaries, affiliates, officers, directors, trustees,
              secretaries, volunteers, employees, agents, representatives,
              successors, and licensees (collectively, “Released Parties”), from
              any and all losses, liabilities, expenses, causes of action, and
              claims for damages (“Claims”) that I may now or in the future
              have. These Claims may arise directly or indirectly from my
              participation in retreat activities; the use of any parking
              locations arranged for the retreat; the use of any shuttle or
              other transportation services provided or arranged; or the
              condition of the property, facilities, or equipment used during
              the retreat. This release applies regardless of when, where, or
              how such Claims arise and whether they are caused by the
              negligence of the Released Parties or otherwise. This includes,
              but is not limited to, Claims relating to:
            </p>
            <ol type="i">
              <li>Any theft, loss, or disappearance of property.</li>
              <li>Bodily injury (fatal or non-fatal).</li>
              <li>Property damage.</li>
            </ol>
            <p style={{ textIndent: "1rem", fontSize: "0.9rem" }}>
              This agreement will be construed broadly to prevent claims and
              lawsuits to the maximum extent permissible under applicable law.
              Any provisions found to be void or unenforceable shall be modified
              or deleted to the minimum extent necessary to make them
              enforceable and shall not affect the enforceability of any other
              provisions.
            </p>
            <p style={{ textIndent: "1rem", fontSize: "0.9rem" }}>
              I have read this agreement, fully understand its terms, and
              recognize that I have given up certain legal rights by signing it
              in consideration of being permitted to participate in retreat
              activities, and to use parking and any shuttle or transportation
              services.
            </p>
            {currentMember.age < 18 ? (
              <p style={{ fontSize: "0.9rem" }}>
                As the participant is a minor, I, the undersigned, am the
                parent/guardian of the participant, and I sign this agreement
                voluntarily and without any inducement or duress.
                <br />
                <br />
                <strong>For Participant :</strong>{" "}
                <u>{`${currentMember.firstName} ${currentMember.lastName}`}</u>
                <br />
                <strong>Parent/Guardian Name:</strong>{" "}
                <u>{`${groupData.firstName} ${groupData.lastName}`}</u>
                <br />
                <strong>Date:</strong> <u>{new Date().toLocaleDateString()}</u>
              </p>
            ) : (
              <p style={{ fontSize: "0.9rem" }}>
                I sign this agreement voluntarily and without any inducement or
                duress.
                <br />
                <br />
                <strong>Participant Name:</strong>{" "}
                <u>{`${currentMember.firstName} ${currentMember.lastName}`}</u>
                <br />
                <strong>Date:</strong> <u>{new Date().toLocaleDateString()}</u>
              </p>
            )}

            {/* Move this outside of the <p> */}
            <div className="d-flex align-items-center gap-1 mt-0">
              <Form.Check
                type="switch"
                id="signature-switch"
                checked={useTypedSignature}
                onChange={toggleSignatureType}
                className="mb-0"
              />
              <Form.Label id="signature-switch-label">
                {"Type your name as a signature"}
              </Form.Label>
            </div>

            <div className="d-flex align-items-center gap-1 mt-0 flex-wrap">
              <Form.Label>
                <strong>Signature : </strong>{" "}
              </Form.Label>

              {useTypedSignature ? (
                <Form.Control
                  type="text"
                  placeholder="Type your name"
                  value={typedSignature}
                  onChange={(e) => setTypedSignature(e.target.value)}
                  className="border border-black rounded p-2 signature-input"
                  style={{
                    width: "100%",
                    maxWidth: "300px",
                    height: "50px",
                    fontFamily: "'Great Vibes', cursive",
                    fontSize: "1.2rem",
                    fontStyle: "italic",
                    textAlign: "center",
                  }}
                />
              ) : (
                <SignatureCanvas
                  penColor="black"
                  penWidth={0.5}
                  id="typed-signature" // Add this ID to find and hide it in the PDF
                  canvasProps={{
                    className: "border border-black rounded",
                    style: { width: "100%", maxWidth: "300px", height: "50px" },
                  }}
                  ref={signatureRef}
                />
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="d-flex flex-column align-items-center gap-2 mt-3">
            {currentMemberIndex < groupData.groupMembers.length - 1 ? (
              <Button onClick={handleNext}>Next</Button>
            ) : (
              <Button onClick={handleSubmit}>Submit Waiver</Button>
            )}
          </div>
        </div>
      )}
      {/* Success Modal */}
      <AlertModal
        show={showSuccessModal}
        title="Successful"
        message={successMessage}
        type="success"
        details={{}} // or pass additional details if necessary
        onCancel={handleCloseModal}
      />
      {/* Error Modal */}
      <AlertModal
        show={showErrorModal}
        title="Error in Retreat"
        type="error"
        message={error}
        details={{}}
        onCancel={handleCloseModal}
      />
    </div>
  );
}
